import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Clock, Link2, Check, List } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatQuarter } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Overline, Rule } from "@/components/site/Primitives";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ConsultCTA } from "@/components/site/ConsultCTA";

function slugifyHeading(text, i) {
  return (
    (text || "")
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .slice(0, 40) || `section-${i}`
  );
}

function processContent(html) {
  if (typeof window === "undefined") return { html, toc: [] };
  const doc = new DOMParser().parseFromString(`<div>${html || ""}</div>`, "text/html");
  const root = doc.body.firstChild;
  const toc = [];
  root.querySelectorAll("h2, h3").forEach((h, i) => {
    const id = slugifyHeading(h.textContent, i);
    h.id = id;
    toc.push({ id, text: h.textContent, level: h.tagName === "H2" ? 2 : 3 });
  });
  // Resolve relative "/api" media sources against the backend origin.
  root.querySelectorAll("img[src], source[src]").forEach((el) => {
    const src = el.getAttribute("src") || "";
    if (src) el.setAttribute("src", mediaUrl(src));
  });
  return { html: root.innerHTML, toc };
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/public/articles/${slug}`)
      .then((r) => {
        if (r.data.redirect) {
          navigate(`/articles/${r.data.redirect}`, { replace: true });
          return;
        }
        setData(r.data);
      })
      .catch(() => setNotFound(true));
  }, [slug, navigate]);

  const processed = useMemo(() => processContent(data?.article?.content), [data]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-32 text-center">
        <h1 className="font-serif text-4xl font-light text-ink">Article not found</h1>
        <p className="mt-4 font-sans text-steel-600">This article may have been moved or is not yet published.</p>
        <Link to="/articles" className="mt-8 inline-flex font-sans text-gold-600 gf-link">Back to all articles →</Link>
      </div>
    );
  }
  if (!data) return <div className="py-40 text-center font-sans text-steel-500">Loading…</div>;

  const a = data.article;
  const showToc = a.show_toc && processed.toc.length > 1;

  return (
    <>
      <Seo
        title={a.seo_title || a.title}
        description={a.seo_description || a.excerpt}
        image={mediaUrl(a.featured_image)}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: a.title,
          description: a.excerpt,
          image: a.featured_image ? [mediaUrl(a.featured_image)] : undefined,
          author: { "@type": "Person", name: a.author },
          publisher: { "@type": "Organization", name: "Goldman Fischer" },
          datePublished: a.published_at,
          dateModified: a.updated_at || a.published_at,
        }}
      />

      <article className="mx-auto max-w-3xl px-5 pt-16 lg:px-0">
        <div className="text-center">
          {a.category && (
            <Link to={`/articles?category=${encodeURIComponent(a.category)}`}>
              <Overline>{a.category}</Overline>
            </Link>
          )}
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight text-ink sm:text-5xl">{a.title}</h1>
          {a.subtitle && <p className="mt-5 font-serif text-2xl font-light italic text-steel-600">{a.subtitle}</p>}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-sans text-sm text-steel-500">
            <span>By {a.author}</span>
            <span>{formatQuarter(a.published_at)}</span>
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {a.reading_time} min read</span>
          </div>
          <Rule className="mx-auto mt-7" />
        </div>

        {a.featured_image && (
          <figure className="mt-10">
            <img src={mediaUrl(a.featured_image)} alt={a.image_alt || a.title} className="w-full" />
            {a.image_alt && <figcaption className="mt-2 text-center font-sans text-xs text-steel-400">{a.image_alt}</figcaption>}
          </figure>
        )}

        {showToc && (
          <nav className="mt-10 border-l-2 border-gold-500/40 bg-cream-200/40 px-6 py-5" data-testid="table-of-contents">
            <p className="flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-gold-600"><List className="h-4 w-4" /> On this page</p>
            <ul className="mt-3 space-y-2">
              {processed.toc.map((t) => (
                <li key={t.id} className={t.level === 3 ? "pl-4" : ""}>
                  <a href={`#${t.id}`} className="font-sans text-sm text-steel-600 hover:text-gold-600">{t.text}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="gf-prose mt-10" dangerouslySetInnerHTML={{ __html: processed.html }} data-testid="article-content" />

        {a.tags?.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2 border-t border-cream-200 pt-8">
            {a.tags.map((t) => (
              <Link key={t} to={`/articles?tag=${encodeURIComponent(t)}`} className="border border-ink/15 px-3 py-1 font-sans text-xs text-steel-600 hover:border-gold-500 hover:text-gold-600">
                #{t}
              </Link>
            ))}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between border-t border-cream-200 pt-8">
          <span className="font-sans text-sm text-steel-500">Share this article</span>
          <button onClick={copyLink} className="flex items-center gap-2 border border-ink/15 px-4 py-2 font-sans text-sm text-ink hover:border-gold-500 hover:text-gold-600" data-testid="copy-link-button">
            {copied ? <Check className="h-4 w-4 text-gold-600" /> : <Link2 className="h-4 w-4" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </article>

      {data.related?.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10">
          <Overline>Related</Overline>
          <h2 className="mb-12 mt-3 font-serif text-3xl font-light text-ink">Continue reading</h2>
          <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {data.related.map((r) => <ArticleCard key={r.id} article={r} />)}
          </div>
        </section>
      )}

      <ConsultCTA
        compact
        heading="Have a decision on your mind?"
        body="If this raised a question about your own situation, request a consultation to discuss it in confidence."
      />
    </>
  );
}
